import { collectPageContext } from '../page-context/parse-page-context.js';
import { getWidgetSession } from '../session/widget-session.js';
import { resolveAnalyticsVisitorIdSync } from './resolve-analytics-visitor-id.js';
import type { WidgetEventName, WidgetEventPayload } from './widget-event-types.js';

export type WidgetAnalyticsEnvelope<T extends WidgetEventName = WidgetEventName> =
  {
    /** Event name (same as GTM `teo_event` when mirrored). */
    event: T;
    /** ISO-8601 timestamp on the client. */
    occurred_at: string;
    /** Per page-load correlation id (UUID when available). */
    page_session_id: string;
    visitor_id: string | null;
    slug: string | null;
    session_id: number | null;
    company_id: number | null;
    thread_id: number | null;
    page_url: string;
    pathname: string;
    locale: string | null;
    page_type: string | null;
    widget_version: string;
    payload: WidgetEventPayload<T>;
  };

const WIDGET_VERSION = '0.0.0';

let pageSessionId: string | null = null;

export function getPageSessionId(): string {
  if (pageSessionId) return pageSessionId;
  pageSessionId =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `teo-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return pageSessionId;
}

export function buildWidgetAnalyticsEnvelope<T extends WidgetEventName>(
  event: T,
  payload: WidgetEventPayload<T>,
  options?: { locale?: string | null; slug?: string | null },
): WidgetAnalyticsEnvelope<T> {
  const session = getWidgetSession();
  const pageUrl =
    typeof window !== 'undefined' ? window.location.href : '';
  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : '/';
  const context =
    typeof window !== 'undefined'
      ? collectPageContext({ url: pageUrl })
      : null;

  return {
    event,
    occurred_at: new Date().toISOString(),
    page_session_id: getPageSessionId(),
    visitor_id: resolveAnalyticsVisitorIdSync(),
    slug: session?.slug ?? options?.slug ?? null,
    session_id: session?.sessionId ?? null,
    company_id: session?.companyId ?? null,
    thread_id: session?.threadId ?? null,
    page_url: pageUrl,
    pathname,
    locale: options?.locale ?? null,
    page_type: context?.pageType ?? null,
    widget_version: WIDGET_VERSION,
    payload,
  };
}
